import { useEffect, useRef, useState } from "react";
import Link from "next/link";
import Logo from "./Logo";
import Button from "./Button";
import Icon from "./Icon";
import Address from "./Address";
import shared from "../content/shared";
import siteConfig from "../lib/siteConfig";
import { useRouter } from "next/router";
import { formatPhoneNumber } from "./phoneUtils.js";

export default function Header({ companyInfo }) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const closeBtnRef = useRef(null);
  const router = useRouter();
  //const cleanedNumber = formatPhoneNumber(companyInfo.phone);

  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
      closeBtnRef.current?.focus();
    } else {
      document.body.style.overflow = "";
    }
  }, [menuOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!menuOpen) return;

      if (e.key === "Escape") {
        setMenuOpen(false);
      } else if (e.key === "Tab") {
        const focusableEls = menuRef.current.querySelectorAll(
          'a, button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
        );
        const first = focusableEls[0];
        const last = focusableEls[focusableEls.length - 1];

        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [menuOpen]);

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-gray-200">
      <div className="flex justify-between items-stretch px-4 md:px-8 relative">
        <Logo className="header-logo py-3 md:py-4" />

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center">
          {siteConfig.navigation.map(({ label, url }) => {
            const isActive = router.pathname === url;
            return (
              <Link
                key={label}
                href={url}
                className={`px-2 py-2 flex items-center ${
                  isActive
                    ? "text-brand-primary font-bold"
                    : "text-gray-800 hover:underline"
                }`}
              >
                {label}
              </Link>
            );
          })}
          {companyInfo?.bookingUrl && (
            <a
              href={`${companyInfo.bookingUrl}`}
              className="ml-4 bg-brand-primary hover:bg-brand-primaryDarker text-white font-medium py-2 px-4 rounded-full shadow transition"
              target="_blank"
              rel="noopener noreferrer"
            >
              {shared.buttons.bookNow}
            </a>
          )}
        </nav>

        {/* Mobile action buttons */}

        <div className="flex items-center gap-4 md:hidden">
          {companyInfo?.bookingUrl && (
            <a
              href={`${companyInfo.bookingUrl}`}
              className="bg-brand-primary hover:bg-brand-primaryDarker text-white font-medium py-2 px-4 rounded-full shadow transition"
            >
              {shared.buttons.bookNow}
            </a>
          )}
          <Button
            className="focus:outline-none"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle navigation"
            variant="circle"
            icon="menu"
          ></Button>
        </div>

        {/* Mobile nav */}
        <nav
          ref={menuRef}
          className={`fixed top-0 right-0 h-screen w-full bg-white flex text-lg flex-col md:hidden z-50 transition-transform duration-300 ease-in-out transform ${
            menuOpen ? "translate-x-0" : "translate-x-full"
          }`}
          inert={!menuOpen}
        >
          <div className="flex justify-between items-center w-full px-4 py-3 border-b">
            <Logo className="" />
            <Button
              onClick={() => setMenuOpen(false)}
              ref={closeBtnRef}
              className="self-center"
              aria-label="Sulje valikko"
              variant="circlePrimary"
              icon="close"
            ></Button>
          </div>
          <div className="flex flex-col gap-4 px-5 pt-4 pb-8">
            {siteConfig.navigation.map(({ label, url }) => {
              const isActive = router.pathname === url;

              return (
                <Link
                  key={label}
                  href={url}
                  onClick={() => setMenuOpen(false)}
                  className={`font-bold ${
                    isActive ? "text-brand-primary font-bold" : "text-gray-800"
                  }`}
                >
                  {label}
                </Link>
              );
            })}
            <hr />
            <div className="flex flex-col gap-3">
              {companyInfo?.bookingUrl && (
                <Button
                  className="flex gap-2 items-center"
                  href={`${companyInfo.bookingUrl}`}
                >
                  <Icon name="calendar" />
                  <span>{siteConfig.bookButton}</span>
                </Button>
              )}
              {companyInfo?.phone && (
                <Button
                  variant="secondary"
                  className="flex gap-2 items-center"
                  href={`tel:${formatPhoneNumber(companyInfo.phone)}`}
                >
                  <Icon name="phone" />
                  {companyInfo.phone}
                </Button>
              )}
              {companyInfo?.email && (
                <Button
                  variant="secondary"
                  className="flex gap-2 items-center"
                  href={`mailto:${companyInfo.email}`}
                >
                  <Icon name="mail" />
                  {companyInfo.email}
                </Button>
              )}
            </div>
            <Address showContact={false} />
          </div>
        </nav>
      </div>
    </header>
  );
}
